import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import {
  classrooms as classroomQuerys,
  assignments as assignmentQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { styled } from "@mui/material/styles";
import {
  getClassrooms,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getCourses } from "@nualang/nualang-api-and-queries/APIs/Courses";
import CreateAssignmentDialog from "@nualang/nualang-ui-components/dist/Assignments/CreateAssignmentDialog";
import { classrooms as classroomHelpers, users as usersHelpers } from "../../../utils/helpers";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { getCourseSectionTopicRoleplays } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicRoleplays";
import config from "../../../config";
import { getClassroomMembers } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import {
  updateAssignment,
  deleteAssignee,
  createAssignees,
} from "@nualang/nualang-api-and-queries/APIs/Assignments";
import dayjs from "dayjs";
import { useApp } from "../../../hooks";

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 3),
  },
}));

function EditAssignment(props) {
  const {
    authData: { username },
  } = props;
  const [assignDialogOpen] = useState(true);
  const queryClient = useQueryClient();
  const { state } = useLocation();
  const { assignment } = state;
  const formattedData = assignment;
  /* eslint-disable react-hooks/immutability */
  formattedData.dueDate = dayjs(formattedData.dueDate);
  formattedData.scheduleDate = dayjs(formattedData.scheduleDate);
  /* eslint-enable react-hooks/immutability */
  const params = useParams();
  const navigate = useNavigate();
  const { classroomId, assignmentId } = params;
  const { t } = useTranslation();
  const { openSnackbar, startLoading, stopLoading } = useApp();

  const getMemberDetails = (membersList) => usersHelpers.fetchMembersDetails(membersList);

  const handleClose = () => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}#Assignments`);
    } else {
      navigate(-1);
    }
  };

  // const classroomAssignmentQuery = assignmentQuerys.useAssignments(
  //   async (classroomId, filters) => {
  //     const assignments = await getClassroomAssignments(classroomId, filters);
  //     return assignments.Items;
  //   },
  //   {
  //     classroomId,
  //     filters: {
  //       classroomId,
  //     },
  //   },
  //   { enabled: !!classroomId },
  // );

  const teachClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        // Get all using high limit
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!username },
  );

  const classrooms =
    teachClassroomsQuery.isSuccess &&
    Array.isArray(teachClassroomsQuery.data) &&
    teachClassroomsQuery.data.length
      ? teachClassroomsQuery.data.filter((c) => c.visibility !== "archived")
      : [];

  const handleUpdateAssignment = async (assignment) => {
    try {
      startLoading();
      const formatted = { ...assignment };
      formatted.type = undefined;
      formatted.classroomId = undefined;
      formatted.assignmentId = undefined;

      const response = await updateAssignment(
        classroomId,
        assignmentId,
        formatted,
      );

      if (response.errorMessage) {
        openSnackbar("Error updating assignment", "error");
        stopLoading();
        return response;
      }

      // determine which assignees were removed (original -> state.assignment)
      const originalAssignees = Array.isArray(
        state?.assignment?.assignedStudents,
      )
        ? state.assignment.assignedStudents
        : [];
      const newAssignees = Array.isArray(assignment.assignedStudents)
        ? assignment.assignedStudents
        : [];

      // compute removed and added assignees (assumes arrays contain comparable primitives or consistent objects)
      const newKeys = new Set(newAssignees);
      const removedAssignees = originalAssignees.filter((a) => !newKeys.has(a));

      const originalKeys = new Set(originalAssignees);
      const addedAssignees = newAssignees.filter((a) => !originalKeys.has(a));

      // delete removed assignees in parallel
      if (removedAssignees.length) {
        const deletePromises = removedAssignees.map((singleAssignee) =>
          deleteAssignee(classroomId, assignmentId, singleAssignee),
        );
        const deleteResults = await Promise.allSettled(deletePromises);
        deleteResults.forEach((r, idx) => {
          if (r.status === "rejected") {
            // eslint-disable-next-line no-console
            console.error(
              "Failed to delete assignee",
              removedAssignees[idx],
              r.reason || r,
            );
          }
        });
      }

      // create newly added assignees (use bulk create if available)
      if (addedAssignees.length) {
        try {
          // createAssignees is expected to accept (classroomId, assignmentId, { assignees: [...] })
          await createAssignees(classroomId, assignmentId, {
            assignees: addedAssignees,
          });
        } catch (err) {
          // If bulk create isn't available or fails, attempt individual creates if createAssignee exists
          // eslint-disable-next-line no-console
          console.error("Bulk create assignees failed, error:", err);
        }
      }
      await invalidateCache(DataType.ASSIGNMENT, {
        classroomId,
        assignmentId,
      });
      queryClient.invalidateQueries({
        queryKey: assignmentQuerys.assignmentKeys.all,
      });
      stopLoading();
      classroomId
        ? navigate(`/classrooms/${classroomId}#Assignments`)
        : navigate("/classrooms");
      openSnackbar("Assignment updated", "success");
      return response.Item;
    } catch (err) {
      console.error(err);
      stopLoading();
      openSnackbar("Error updating assignment", "error");
    }
  };

  return (
    <Root>
      <CreateAssignmentDialog
        t={t}
        dialogTitle={"update_assignment"}
        initialData={assignment}
        open={assignDialogOpen}
        classrooms={classrooms}
        classroom={classroomId}
        getCourseSections={getCourseSections}
        getRoleplays={getCourseSectionTopicRoleplays}
        username={username}
        createAssignment={handleUpdateAssignment}
        handleClose={handleClose}
        getClassroomMembers={getClassroomMembers}
        getMemberDetails={getMemberDetails}
        getCourses={getCourses}
      />
    </Root>
  );
}

EditAssignment.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
};

export default EditAssignment;
